import type { PersonAccountLink, ProfileManagementGrant, UUID } from './types';
// Type-only, so this module stays free of a runtime dependency on permissions.ts
// (which imports this one).
import type { Actor } from './permissions';

/**
 * Who is responsible for a circle.
 *
 *   'self'      — the person themselves. They signed in and claimed their own
 *                 circle, and nobody else may change their picture or details.
 *                 An administrator cannot hand this to someone else: it is the
 *                 whole point of claiming it.
 *   'caretaker' — somebody looking after a circle whose person has not joined
 *                 (a grandmother who does not use the app, a relative who has
 *                 passed away, a child). Whoever adds a person becomes their
 *                 caretaker, and an administrator can hand it to someone else.
 *
 * A circle with neither is unowned: anyone in the family may add a photo to it,
 * which is how the tree gets filled in. New circles are never unowned — the
 * person adding them becomes the caretaker on the spot.
 */
export type OwnerKind = 'self' | 'caretaker';

export interface CircleOwner {
  accountId: UUID;
  kind: OwnerKind;
  /** The grant that carries a caretaker's responsibility (absent for 'self'). */
  grantId?: UUID;
}

/** An active, full-scope caretaker grant — `record_only` helpers are not owners. */
function caretakerGrant(grants: ProfileManagementGrant[], personId: UUID): ProfileManagementGrant | undefined {
  return grants.find((g) => g.managedPersonId === personId && g.status === 'active' && g.scope === 'full');
}

/** Who looks after this circle right now, or null when nobody does yet. */
export function circleOwner(links: PersonAccountLink[], grants: ProfileManagementGrant[], personId: UUID): CircleOwner | null {
  const self = links.find((l) => l.personId === personId && l.status === 'active');
  if (self) return { accountId: self.accountId, kind: 'self' };
  const grant = caretakerGrant(grants, personId);
  if (grant) return { accountId: grant.helperAccountId, kind: 'caretaker', grantId: grant.id };
  return null;
}

/** Is this the account that looks after the circle? */
export function ownsCircle(actor: Actor, links: PersonAccountLink[], grants: ProfileManagementGrant[], personId: UUID): boolean {
  return circleOwner(links, grants, personId)?.accountId === actor.session.accountId;
}

/** Everyone the family has asked to manage this profile (a helper for an elderly relative, a guardian). */
export function circleHelpers(grants: ProfileManagementGrant[], personId: UUID): ProfileManagementGrant[] {
  return grants.filter((g) => g.managedPersonId === personId && g.status === 'active' && g.scope === 'full');
}

/**
 * Whether this actor may change the circle's photo and details without asking.
 *
 * Its owner, of course; an administrator with Admin mode on; and anyone at all
 * while nobody has taken the circle on, which is how a tree of people who never
 * joined gets its faces.
 *
 * Also a helper the family gave the profile to — a granddaughter looking after
 * her grandmother's circle. That is not a stranger changing someone's face: it
 * was granted deliberately, it is listed on the profile, and an administrator
 * can take it back.
 */
export function mayEditCircle(actor: Actor, links: PersonAccountLink[], grants: ProfileManagementGrant[], personId: UUID): boolean {
  return circleEditRight(actor, links, grants, personId) !== 'none';
}

/**
 * *Why* this actor may change the circle — not just whether.
 *
 *   'own' | 'helper' | 'unowned' — an ordinary right anyone in their position has
 *   'admin'                     — they may only because Admin mode is on
 *   'none'                      — they may not
 *
 * The UI needs the distinction: a control that exists only because you are an
 * administrator is painted in the admin colour, so that following the purple
 * always leads to a power ordinary members do not have.
 */
export type CircleEditRight = 'own' | 'helper' | 'unowned' | 'admin' | 'none';

export function circleEditRight(actor: Actor, links: PersonAccountLink[], grants: ProfileManagementGrant[], personId: UUID): CircleEditRight {
  const owner = circleOwner(links, grants, personId);
  if (owner?.accountId === actor.session.accountId) return 'own';
  if (circleHelpers(grants, personId).some((g) => g.helperAccountId === actor.session.accountId)) return 'helper';
  if (!owner) return 'unowned';
  return actor.session.role === 'admin' ? 'admin' : 'none';
}

/**
 * Whether this actor may change the **face** on a circle, and why.
 *
 * Stricter than `circleEditRight` in one deliberate place, and the difference
 * is the whole rule:
 *
 *   • Your own circle, or one you look after — always yours to change.
 *   • A circle **nobody has taken on** and that has **no face yet**: anyone in
 *     the family may give it its first one. That is how a tree of ancestors who
 *     never joined the app gets its pictures, and it is the reason the loose
 *     rule existed in the first place.
 *   • The same circle **once it has a face**: only its keeper or an
 *     administrator. Putting the first photograph on Saba Moshe is a gift;
 *     replacing the one his daughter chose is somebody else's decision.
 *   • A circle somebody else keeps: an administrator only.
 *
 * `'admin'` means they may *only* because Admin mode is on, which is what
 * tells the UI to paint the camera in the admin colour — so following the
 * purple always leads to a power ordinary members do not have.
 *
 * Jay, on finding a camera on sixteen circles he had nothing to do with:
 * *"only the ones I am responsible for … unless I enable that admin button,
 * in that case to modify all of them — but then that camera icon should be
 * highlighted to show it's an admin control."*
 */
export function circlePortraitRight(
  actor: Actor,
  links: PersonAccountLink[],
  grants: ProfileManagementGrant[],
  person: { id: UUID; portraitUrl?: string },
): CircleEditRight {
  const right = circleEditRight(actor, links, grants, person.id);
  if (right !== 'unowned') return right;
  if (!person.portraitUrl) return 'unowned';
  return actor.session.role === 'admin' ? 'admin' : 'none';
}

/**
 * Whether an administrator may hand this circle to a different caretaker.
 * A circle its own person has claimed is theirs, and stays theirs.
 */
export function canReassignCircle(links: PersonAccountLink[], personId: UUID): boolean {
  return !links.some((l) => l.personId === personId && l.status === 'active');
}

/**
 * Whether this actor may edit a person's *details*, and why.
 *
 * Details are stricter than a photo, in two ways:
 *
 *   • A locked circle — someone who has passed away, a record checked against
 *     the family book — is an administrator's to change and nobody else's,
 *     whoever looks after it.
 *   • A circle nobody has taken on is not "yours to correct" either. Adding a
 *     face to an ancestor nobody claimed is how the tree gets its pictures; a
 *     stranger rewriting their dates is a different thing. An administrator
 *     edits it, or gives it an owner first (`setCircleOwner`).
 *
 * What remains is: your own circle, one you look after — and administrators,
 * who may edit anyone. `'admin'` means they may *only* because Admin mode is
 * on, which is what tells the UI to paint the Edit button in the admin colour.
 */
export function circleDetailsRight(
  actor: Actor,
  links: PersonAccountLink[],
  grants: ProfileManagementGrant[],
  person: { id: UUID; locked?: boolean },
): CircleEditRight {
  const admin = actor.session.role === 'admin';
  if (person.locked) return admin ? 'admin' : 'none';
  const right = circleEditRight(actor, links, grants, person.id);
  if (right === 'unowned' || right === 'none') return admin ? 'admin' : 'none';
  return right;
}
